import {Application, Context} from 'egg';
import RootStore, {dataStore} from '../../components/common/rootStore';
import {ICommonCommentItem, ICommonReplyItem} from '../../components/../apis/comment/commonApiAdapter';
import {action, observable, reaction, runInAction, toJS} from 'mobx';
import {IStorage, LocalStorage} from '../../components/common/storage';
import {ICommentCreateData} from 'uciflow';
import {AContainerStoreBase} from '../../base/store';
import {addGlobalEventHandler, triggerGlobalEvent} from '../../common/globalEventHandle';
import {postJsonWithCommonParams} from '../../common/request';
import {callSdkMethod} from '../../common/sdkWrap';
import {commonParamBuilder} from '../../../lib/paramBuilder';
import queryString from 'query-string';
import * as uuid from 'uuid/v1';
import {L} from '../../lang';
import {testComment} from '../../components/container/commentInput/test';

export interface ICommentInputStoreEventType {
  'commentInput:add:comment': {
    newItem: ICommonCommentItem;
  };
  'commentInput:add:reply': {
    newItem: ICommonReplyItem;
  };
  'commentInput:init:history': {
    commentItems: ICommonCommentItem[];
    replyItems: ICommonReplyItem[];
  };
  'commentInput:open:replyComment': {
    commentItem: ICommonCommentItem;
  };
  'commentInput:open:replyReply': {
    replyItem: ICommonReplyItem;
  };
}

export interface ICommentInputModel {
  userId: string;
  itemId: string;
  commentId?: string;
  defaultNames: string[];
  instance: string;
}

// @dataStore<ICommentInputModel>('container.commentInput.store')
export default class CommentInputStore extends AContainerStoreBase {
  public itemId;
  public commentId;
  public userId;
  public defaultNames ;

  @observable
  public commentItems: ICommonCommentItem[];

  @observable
  public replyItems: ICommonReplyItem[];

  private rootStore;
  @observable
  private status: 'init' | 'ready' | 'waiting';

  private storage: IStorage;

  private getCurrentReplyItem: () => ICommonReplyItem;

  constructor(rootStore: any, initParams?: {itemId; commentId; userId}) {
    super();
    this.rootStore = rootStore;
    this.status = 'init';
    this.commentItems = [];
    this.replyItems = [];
    if (process.env.BROWSER) {
      this.storage = new LocalStorage('comment/input');

      addGlobalEventHandler('commentInput:open:replyComment', this.onOpenReplyInput);
      addGlobalEventHandler('commentInput:open:replyReply', this.onOpenReplyInput);
      // this.init();

    }

    if ( initParams) {
      this.itemId = initParams.itemId;
      this.commentId = initParams.commentId;
      this.userId = initParams.userId;
    }
  }

  public async ssrInit(ctx: Context, initParams: {itemId; commentId; }): Promise<any> {
    // super.ssrInit(ctx, otherParams);
    this.itemId = initParams.itemId;
    this.commentId = initParams.commentId;

    const commonParams = commonParamBuilder.getParams(ctx.query);
    this.userId = commonParams.ut || commonParams.ds || commonParams.dn;
    if (!this.userId) {
      this.userId = uuid();
    }
  }

  public toJson(): any {
    // return super.toJson();
    return {
      itemId: this.itemId,
      commentId: this.commentId,
      userId: this.userId,
    };
  }

  @action
  public addCommentItem = async (data: ICommentCreateData) => {
    if (!data.message) {
      throw new Error('emoty message');
    }
    data = this.toApiCommentData(data);
    const newItem = this.toCommentItem(data);
    this.commentItems.push(newItem);
    triggerGlobalEvent('commentInput:add:comment', {
      data: {
        newItem,
      },
    });
    if (this.status !== 'ready') {
      await this.waitReady();
    }
    this.status = 'waiting';
    const saveItemPromise = this.saveItem('comment');
    const postJsonPromise = postJsonWithCommonParams(`/api/comment/add/comment/story`, data);
    await Promise.all([saveItemPromise, postJsonPromise]);
    runInAction(() => {
      this.status = 'ready';
    });
  }

  @action
  public addReplyItem = async (data: ICommentCreateData) => {
    if (!data.message) {
      throw new Error('emoty message');
    }
    data = this.toApiCommentData(data);
    const newItem = this.toReplyItem(data);
    this.replyItems.push(newItem);
    triggerGlobalEvent('commentInput:add:reply', {
      data: {
        newItem,
      },
    });
    if (this.status !== 'ready') {
      await this.waitReady();
    }
    this.status = 'waiting';
    const saveItemPromise = this.saveItem('reply');
    const postJsonPromise = postJsonWithCommonParams(`/api/comment/add/reply/story`, data);
    await Promise.all([saveItemPromise, postJsonPromise]);
    runInAction(() => {
      this.status = 'ready';
    });
  }

  @action
  public init = async () => {
    this.defaultNames = [L('comment.default_user')];
    // console.log('defaultNames', this.defaultNames);
    this.commentItems = await this.storage.getItem<ICommonCommentItem[]>(`comment/${this.itemId}`) || [];
    this.replyItems = await this.storage.getItem<ICommonReplyItem[]>(`reply/${this.itemId}`) || [];
    runInAction(() => {
      this.status = 'ready';
      triggerGlobalEvent('commentInput:init:history', {
        data: {
          commentItems: this.commentItems,
          replyItems: this.replyItems,
        },
      });
    });

    window._testComment = () => {this.addCommentItem(testComment as any); };
  }

  private toApiCommentData = (data: ICommentCreateData): ICommentCreateData => {
    data.item_id = this.itemId;
    data.comment_id = this.commentId;
    data.user_image = this.getUserImage(data.user_image);
    data.user_name = this.getUserName(data.user_name);
    if (this.getCurrentReplyItem) {
      const currentReplyItem = this.getCurrentReplyItem();
      if (currentReplyItem) {
        data.message = `${data.message} //@${currentReplyItem.userName}:${currentReplyItem.message}`;
      }
    }
    return data;
  }

  private toCommentItem = (data: ICommentCreateData): ICommonCommentItem => {
    return {
      itemId: this.itemId,
      commentId: `${Date.now()}`,
      userId: data.user_id,
      userImage: data.user_image,
      userName: data.user_name,
      publishTime: Date.now(),
      isWemediaUser: false,
      isWemediaAuthor: false,
      message: data.message,
      replyCount: 0,
      like: 0,
      isLocal: true,
    };
  }

  private toReplyItem = (data: ICommentCreateData): ICommonReplyItem => {
    return {
      itemId: this.itemId,
      commentId: this.commentId,
      replyId: `${Date.now()}`,
      userId: data.user_id,
      userImage: data.user_image,
      userName: data.user_name,
      publishTime: Date.now(),
      isWemediaUser: false,
      isWemediaAuthor: false,
      message: data.message,
      isLocal: true,
    };
  }

  private waitReady = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      reaction(() => this.status, () => {
        if (this.status === 'ready') {
          resolve(true);
        }
      });
    });
  }

  private saveItem = async (type: 'comment' | 'reply') => {
    if (type === 'comment') {
      this.storage.setItem(`${type}/${this.itemId}`, toJS(this.commentItems));
    } else if (type === 'reply') {
      this.storage.setItem(`${type}/${this.itemId}/${this.commentId}`, toJS(this.replyItems));
    }
  }

  private getUserImage = (userImage: string) => {
    if (userImage && userImage.indexOf('uclocal://') !== -1) {
      return `http://img.ucweb.com/s/uae/g/1s/comment/img/avatar/${userImage.replace('uclocal://', '')}`;
    } else if (userImage && userImage.indexOf('http') !== -1) {
      return userImage;
    }
    const defaultUserImageHost = 'http://img.ucweb.com/s/uae/g/1s/comment/img/';
    const defaultUserImages = ['1-min.png', '2-min.png', '3-min.png', '4-min.png', '5-min.png', '6-min.png', '7-min.png', '8-min.png', '9-min.png', '10-min.png'];
    let imageKeyNum = 0;
    const userId = this.userId;
    if (!userId) {
      imageKeyNum = Math.floor(Math.random() * 100);
    } else {
      const userIdKey = userId.replace(/[^a-zA-Z0-9]/g, '');
      for (let i = 0; i < userIdKey.length; i++) {
        imageKeyNum += userIdKey.charCodeAt(i);
      }
    }
    return defaultUserImageHost + defaultUserImages[imageKeyNum % defaultUserImages.length];
  }

  private getUserName = (defaultUserName?: string) => {
    if (defaultUserName) {
      return defaultUserName;
    }
    const userId = this.userId;
    const defaultNames = this.defaultNames;
    let keyNum = 0;
    if (!userId) {
      keyNum = Math.floor(Math.random() * 100);
    } else {
      const userIdKey = userId.replace(/[^a-zA-Z0-9]/g, '');
      for (let i = 0; i < userIdKey.length; i++) {
        keyNum += userIdKey.charCodeAt(i);
      }
    }
    if (defaultNames && defaultNames.length > 0) {
      return defaultNames[keyNum % defaultNames.length];
    } else {
      return '';
    }
  }

  private onOpenReplyInput = (event) => {
    const data = event.data;
    if ('commentItem' in data) {
      const item = data.commentItem as ICommonCommentItem;
      callSdkMethod('comment', 'openCommentInput')({
        type: 1,
        comment_id: item.commentId,
        comment_ref_id: `${item.itemId}_comment`,
        reply_user_name: `${item.userName}`,
        reply_user_id: `${item.userId}`,
      });
    } else if ('replyItem' in data) {
      const item = data.replyItem as ICommonReplyItem;
      this.getCurrentReplyItem = () => {
        this.getCurrentReplyItem = null;
        return item;
      };
      callSdkMethod('comment', 'openCommentInput')({
        type: 1,
        comment_id: item.commentId,
        comment_ref_id: `${item.itemId}_comment`,
        reply_id: `${item.replyId}`,
        reply_user_name: `${item.userName}`,
        reply_user_id: `${item.userId}`,
      });
    }
  }

}
